import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    Grid,
    TextField,
    Slider,
    Typography
} from "@mui/material/";
import { strings } from "../../../../common/i18n";
import actions from "../../../../Store/actions";
import { SelectAll } from "../../../../common/components";
import makeStyles from "@mui/styles/makeStyles";
import styles from "./styles";

/**
 * Audit Details Component
 */
export default ({ isDisabled }) => {
    const { auditsCreate, typesList } = useSelector((state) => state);
    // Start from what this wizard has already saved. Every handler below
    // writes to auditsCreate.filters, and this step remounts on each Back/Next,
    // so reading the initial values from anywhere else (it used to read the
    // audit-filters slice, which holds none of these) reset the monetary
    // value, the sampling slider and the New/Used ticks on screen every time —
    // while AuditSummary, which reads auditsCreate.filters, still showed the
    // old values. Caught by T-B7 (timing/create-wizard.spec.js), 2026-10-06.
    const savedFilters = auditsCreate.filters || {};
    const classes = makeStyles(styles)();

    const dispatch = useDispatch();
    const [assetRandomSampling, setAssetRandomSampling] = useState(
        savedFilters.randomValueSampling ?? 100
    );
    const [monetaryValue, setMonetaryValue] = useState(
        savedFilters.monetaryValueSampling ?? 0
    );
    const [condition, setCondition] = useState({
        isUsed: savedFilters.conditions?.isUsed ?? true,
        isNew: savedFilters.conditions?.isNew ?? true
    });

    const handleUsedChange = () => {
        setCondition({
            ...condition,
            isUsed: !condition.isUsed
        });
        dispatch(
            actions.auditsCreate.create.itemSet({
                filters: {
                    ...auditsCreate.filters,
                    conditions: { ...condition, isUsed: !condition.isUsed }
                }
            })
        );
    };
    const handleNewChange = () => {
        setCondition({
            ...condition,
            isNew: !condition.isNew
        });
        dispatch(
            actions.auditsCreate.create.itemSet({
                filters: {
                    ...auditsCreate.filters,
                    conditions: { ...condition, isNew: !condition.isNew }
                }
            })
        );
    };

    /** Clear filters when the user switches into the "assets to audit" tab
     * (isDisabled flips true). Skip the very first render — this component
     * remounts every time the wizard steps Back/Next through step 5, and
     * without this guard that remount wiped monetaryValueSampling back to 0
     * even though the user never touched the toggle. */
    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        setCondition({
            isNew: true,
            isUsed: true
        });
        setAssetRandomSampling(100);
        setMonetaryValue(0);
        // Only this step's own controls are reset here — NOT the store.
        // ReturnAuditDetails' radio handler already writes the right filters
        // for each choice ({ assetsToAudit: [] } for "specific assets", these
        // same defaults for "audit details"). Dispatching here as well ran
        // AFTER it and replaced the whole `filters` object, deleting
        // assetsToAudit; AuditSummary's CREATE gate then saw no list (rather
        // than an empty one) and enabled CREATE, so an audit meant for pasted
        // VINs could be created for every asset at 100% sampling. Caught by
        // Playwright T-B6 against QA, 2026-10-08.
    }, [isDisabled]);

    const handleChange = (name: string) => (event: Event) => {
        dispatch(
            actions.auditsCreate.create.itemSet({
                filters: { ...auditsCreate.filters, [name]: event }
            })
        );
    };

    const hdlMonetaryValue = (event) => {
        const val = event.target.value;
        setMonetaryValue(parseInt(val));
        if (Number.isInteger(parseInt(val))) {
            dispatch(
                actions.auditsCreate.create.itemSet({
                    filters: {
                        ...auditsCreate.filters,
                        monetaryValueSampling: parseInt(val)
                    }
                })
            );
        }
    };

    const hdlAssetRandomSamplingChange = (event, newValue) => {
        setAssetRandomSampling(newValue);
        dispatch(
            actions.auditsCreate.create.itemSet({
                filters: {
                    ...auditsCreate.filters,
                    randomValueSampling: newValue
                }
            })
        );
    };

    return (
        <>
            <Grid item xs={12}>
                <FormControl fullWidth variant="standard">
                    <SelectAll
                        disabled={isDisabled}
                        items={typesList.assetTypes.data}
                        onChange={handleChange("assetTypes")}
                        title={strings("assetTypes")}
                        value={auditsCreate.filters.assetTypes}
                        defaultValue={auditsCreate.filters.assetTypes}
                    />
                </FormControl>
            </Grid>

            <Grid item xs={12}>
                <FormControl fullWidth variant="standard" margin="dense">
                    <SelectAll
                        disabled={isDisabled}
                        items={typesList.categories.data}
                        onChange={handleChange("categories")}
                        title={strings("categories")}
                        value={auditsCreate.filters.categories}
                        defaultValue={auditsCreate.filters.categories}
                    />
                </FormControl>
            </Grid>

            <Grid item xs={12}>
                <FormControl fullWidth variant="standard" margin="dense">
                    <SelectAll
                        disabled={isDisabled}
                        items={typesList.makes.data}
                        onChange={handleChange("makes")}
                        title={strings("makes")}
                        value={auditsCreate.filters.makes}
                        defaultValue={auditsCreate.filters.makes}
                    />
                </FormControl>
            </Grid>

            <Grid item xs={12}>
                <FormControl fullWidth variant="standard" margin="dense">
                    <SelectAll
                        disabled={isDisabled}
                        items={typesList.financeTypes.data}
                        onChange={handleChange("financeTypes")}
                        value={auditsCreate.filters.financeTypes}
                        title={strings("financeType")}
                        defaultValue={auditsCreate.filters.financeTypes}
                    />
                </FormControl>
            </Grid>

            <Grid item xs={12}>
                <Typography
                    disabled={isDisabled}
                    className={classes.label}
                    id="assetRandomSampling"
                    gutterBottom
                >
                    {strings("assetRandomSampling")}
                </Typography>
                <Slider
                    disabled={isDisabled}
                    value={assetRandomSampling}
                    onChange={hdlAssetRandomSamplingChange}
                    valueLabelDisplay="auto"
                    aria-labelledby="assetRandomSampling"
                />
            </Grid>

            <Grid item xs={12}>
                <FormGroup>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        disabled={isDisabled}
                                        checked={condition.isUsed}
                                        onChange={handleUsedChange}
                                        color="primary"
                                    />
                                }
                                label={strings("used")}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        disabled={isDisabled}
                                        onChange={handleNewChange}
                                        checked={condition.isNew}
                                        color="primary"
                                    />
                                }
                                label={strings("new")}
                            />
                        </Grid>
                    </Grid>
                </FormGroup>
            </Grid>

            <Grid item xs={12}>
                <FormControl fullWidth>
                    <TextField
                        disabled={isDisabled}
                        id="monetaryValue"
                        type="number"
                        label={`${strings("monetaryValue")}`}
                        aria-describedby="monetaryValue"
                        onChange={hdlMonetaryValue}
                        value={monetaryValue}
                    />
                </FormControl>
            </Grid>
        </>
    );
};
